"use client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Story } from "@/lib/types/story";
import { ArrowLeft, ArrowRight, Search } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from 'react';
import StoryCard from "../shared/StoryCard";
import { Stagger, StaggerItem } from "@/components/motion";

const ITEMS_PER_PAGE = 9;

const StoriesPage = ({
  searchParams,
  stories,
}: {
  searchParams: { page?: string; search?: string; sort?: string };
  stories: Story[];
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const filteredStories = useMemo(() => {
    let filtered = [...stories];
    if (search) {
      filtered = filtered.filter((story) => story.title.toLowerCase().includes(search.toLowerCase()));
    }
    if (sort === "newest") {
      filtered = filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sort === "oldest") {
      filtered = filtered.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
    return filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }, [stories, search, sort, currentPage]);

  return (
    <>
      {/* Filters and Search */}
      <div className="mb-8 flex sm:justify-end">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-1/2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search stories..."
              className="pl-10"
              defaultValue={search}
              onChange={handleSearch}
            />
          </div>
          <Select
            defaultValue={sort}
            onValueChange={(value) => {
              setSort(value);
            }}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              {/* <SelectItem value="popular">Most Popular</SelectItem> */}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stories Grid */}
      <Stagger className="auto-grid xl:auto-grid-lg sm:auto-grid-md gap-6 mb-8">
        {filteredStories.map((story) => (
          <StaggerItem key={story.id} className="h-full">
            <Link href={`/stories/${story.id}`} className="h-full block">
              <StoryCard story={story} />
            </Link>
          </StaggerItem>
        ))}
      </Stagger>

      {/* Pagination */}
      <div className="flex justify-center items-center gap-4">
        <Button
          variant="outline"
          disabled={currentPage === 1}
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set("page", (currentPage - 1).toString());
            window.location.href = url.toString();
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        <span className="text-gray-600">Page {currentPage}</span>
        <Button
          variant="outline"
          disabled={filteredStories.length < ITEMS_PER_PAGE}
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set("page", (currentPage + 1).toString());
            window.location.href = url.toString();
          }}
        >
          Next
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </>
  );
};

export default StoriesPage;
